import {useMemo} from 'react'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {toast} from 'sonner'
import ReferentialService from '../service/referential.services'
import {
  PriorityTrackOption,
  toPriorityTrackOptions,
} from '../utils/priorityTracks.functions'
import {
  TCreateTrackLabel,
  TCreateTrackSegment,
  TDeclareSwitchConnection,
  TNetworkTrackLabel,
  TQualifySwitchConnection,
  TSwitchConnection,
  TTrackSegment,
  TUpdateTrackLabel,
  TUpdateTrackSegment,
} from '../types/referential.type'
import {ErrorHelper} from '../types/app.type'
import {getTranslation} from '../utils/getTranslation'

const referentialKeys = {
  tracks: (networkId: string) => ['referential', 'tracks', networkId] as const,
  switches: (networkId: string) =>
    ['referential', 'switches', networkId] as const,
}

/**
 * Invalidate everything a referential write can move.
 *
 * The switch caches are in scope because declaring or qualifying a connection
 * writes a `Switch` row: the settings switch table and the geo-zone payload
 * would otherwise keep serving the previous state.
 *
 * The linear-view viewport is in scope too: its blocks are a server projection
 * that already carries the tracks/segments, so a new label/segment must refetch
 * it. Its query key embeds the PK window, and adding a parallel track that fits
 * the existing extent leaves that window (hence the key) unchanged — so only an
 * explicit prefix invalidation shows the new lane without a full page reload.
 */
const invalidateReferential = (
  queryClient: ReturnType<typeof useQueryClient>,
  networkId: string,
) => {
  queryClient.invalidateQueries({queryKey: referentialKeys.tracks(networkId)})
  queryClient.invalidateQueries({queryKey: referentialKeys.switches(networkId)})
  queryClient.invalidateQueries({queryKey: ['switches']})
  queryClient.invalidateQueries({queryKey: ['network', 'withGeoZones']})
  queryClient.invalidateQueries({queryKey: ['network', 'viewport', networkId]})
  // A whole track designates its segments: a moved segment moves its sections.
  queryClient.invalidateQueries({
    queryKey: ['network', 'designables', networkId],
  })
}

export const useGetReferentialTracks = (networkId: string | undefined) => {
  const {data, isLoading} = useQuery<TNetworkTrackLabel[], ErrorHelper>({
    queryKey: referentialKeys.tracks(networkId ?? ''),
    queryFn: () => ReferentialService.getTracks(networkId as string),
    enabled: Boolean(networkId),
    // Reference data: invalidated by every mutation in this file.
    staleTime: Infinity,
  })
  return {data, isLoading}
}

/**
 * Live network track labels shaped as activity-priority options.
 *
 * The priority cell must offer only tracks that still exist, so it sources
 * from the track-first referential (server-filtered to `deletedAt: null`)
 * rather than the legacy per-project `Track` list, which is never pruned on a
 * label delete and would keep surfacing soft-deleted labels (e.g. "N/A").
 */
export const usePriorityTrackOptions = (
  networkId: string | undefined,
): PriorityTrackOption[] => {
  const {data} = useGetReferentialTracks(networkId)
  return useMemo(() => toPriorityTrackOptions(data), [data])
}

export const useGetNetworkSwitches = (networkId: string | undefined) => {
  const {data, isLoading} = useQuery<TSwitchConnection[], ErrorHelper>({
    queryKey: referentialKeys.switches(networkId ?? ''),
    queryFn: () => ReferentialService.getNetworkSwitches(networkId as string),
    enabled: Boolean(networkId),
    // Reference data: invalidated by every mutation in this file.
    staleTime: Infinity,
  })
  return {data, isLoading}
}

export const useCreateReferentialTrack = (networkId: string) => {
  const queryClient = useQueryClient()
  const {mutateAsync, isPending, error} = useMutation<
    TNetworkTrackLabel | undefined,
    ErrorHelper,
    TCreateTrackLabel
  >({
    mutationFn: track => ReferentialService.createTrack(networkId, track),
    onSuccess: () => {
      invalidateReferential(queryClient, networkId)
      toast.success(getTranslation('referential:trackCreated'))
    },
    onError: () => toast.error(getTranslation('referential:trackCreateError')),
  })
  return {createTrack: mutateAsync, isPending, error}
}

export const useUpdateReferentialTrack = (networkId: string) => {
  const queryClient = useQueryClient()
  const {mutateAsync, isPending, error} = useMutation<
    TNetworkTrackLabel | undefined,
    ErrorHelper,
    {id: string; track: TUpdateTrackLabel}
  >({
    mutationFn: ({id, track}) =>
      ReferentialService.updateTrack(networkId, id, track),
    onSuccess: () => {
      invalidateReferential(queryClient, networkId)
      toast.success(getTranslation('referential:trackUpdated'))
    },
    onError: () => toast.error(getTranslation('referential:trackUpdateError')),
  })
  return {updateTrack: mutateAsync, isPending, error}
}

export const useDeleteReferentialTrack = (networkId: string) => {
  const queryClient = useQueryClient()
  const {mutateAsync, isPending, error} = useMutation<
    unknown,
    ErrorHelper,
    string
  >({
    mutationFn: id => ReferentialService.deleteTrack(networkId, id),
    onSuccess: () => {
      invalidateReferential(queryClient, networkId)
      toast.success(getTranslation('referential:trackDeleted'))
    },
    // A refused delete is the common case, not an anomaly: the label is still
    // carrying segments or switches, and the message has to say so.
    onError: () => toast.error(getTranslation('referential:trackInUseError')),
  })
  return {deleteTrack: mutateAsync, isPending, error}
}

export const useCreateReferentialSegment = (networkId: string) => {
  const queryClient = useQueryClient()
  const {mutateAsync, isPending, error} = useMutation<
    TTrackSegment | undefined,
    ErrorHelper,
    {trackId: string; segment: TCreateTrackSegment}
  >({
    mutationFn: ({trackId, segment}) =>
      ReferentialService.createSegment(networkId, trackId, segment),
    onSuccess: () => {
      invalidateReferential(queryClient, networkId)
      toast.success(getTranslation('referential:segmentCreated'))
    },
    onError: () => toast.error(getTranslation('referential:segmentSaveError')),
  })
  return {createSegment: mutateAsync, isPending, error}
}

export const useUpdateReferentialSegment = (networkId: string) => {
  const queryClient = useQueryClient()
  const {mutateAsync, isPending, error} = useMutation<
    unknown,
    ErrorHelper,
    {id: string; segment: TUpdateTrackSegment}
  >({
    mutationFn: ({id, segment}) =>
      ReferentialService.updateSegment(networkId, id, segment),
    onSuccess: () => {
      invalidateReferential(queryClient, networkId)
      toast.success(getTranslation('referential:segmentUpdated'))
    },
    onError: () => toast.error(getTranslation('referential:segmentSaveError')),
  })
  return {updateSegment: mutateAsync, isPending, error}
}

export const useDeleteReferentialSegment = (networkId: string) => {
  const queryClient = useQueryClient()
  const {mutateAsync, isPending, error} = useMutation<
    unknown,
    ErrorHelper,
    string
  >({
    mutationFn: id => ReferentialService.deleteSegment(networkId, id),
    onSuccess: () => {
      invalidateReferential(queryClient, networkId)
      toast.success(getTranslation('referential:segmentDeleted'))
    },
    onError: () => toast.error(getTranslation('referential:segmentSaveError')),
  })
  return {deleteSegment: mutateAsync, isPending, error}
}

export const useDeclareSwitchConnection = (networkId: string) => {
  const queryClient = useQueryClient()
  const {mutateAsync, isPending, error} = useMutation<
    unknown,
    ErrorHelper,
    TDeclareSwitchConnection
  >({
    mutationFn: connection =>
      ReferentialService.declareSwitch(networkId, connection),
    onSuccess: () => {
      invalidateReferential(queryClient, networkId)
      toast.success(getTranslation('referential:connectionCreated'))
    },
    onError: () =>
      toast.error(getTranslation('referential:connectionSaveError')),
  })
  return {declareConnection: mutateAsync, isPending, error}
}

export const useQualifySwitchConnection = (networkId: string) => {
  const queryClient = useQueryClient()
  const {mutateAsync, isPending, error} = useMutation<
    unknown,
    ErrorHelper,
    {id: string; connection: TQualifySwitchConnection}
  >({
    mutationFn: ({id, connection}) =>
      ReferentialService.qualifySwitch(networkId, id, connection),
    onSuccess: () => {
      invalidateReferential(queryClient, networkId)
      toast.success(getTranslation('referential:connectionUpdated'))
    },
    onError: () =>
      toast.error(getTranslation('referential:connectionSaveError')),
  })
  return {qualifyConnection: mutateAsync, isPending, error}
}

export const useDeleteSwitchConnection = (networkId: string) => {
  const queryClient = useQueryClient()
  const {mutateAsync, isPending, error} = useMutation<
    unknown,
    ErrorHelper,
    string
  >({
    mutationFn: id => ReferentialService.deleteSwitchConnection(networkId, id),
    onSuccess: () => {
      invalidateReferential(queryClient, networkId)
      toast.success(getTranslation('referential:connectionDeleted'))
    },
    // Refused when the connection derives from a project junction: the double
    // write owns those, and removing one means removing its junction.
    onError: () =>
      toast.error(getTranslation('referential:connectionDerivedError')),
  })
  return {deleteConnection: mutateAsync, isPending, error}
}
